import { VoiceComposer } from "./voice-composer";
import { useHandlerCompanionEnabled } from "@/features/stepper/steptypes/companion-config";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Paperclip, ArrowUp, Smile, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useChatStore } from "@/store/chat";
import { useUpload } from "../api/use-chat";
import { REACTIONS } from "../lib/messages";
import type {
  Attachment,
  Message,
  Participant,
  PendingMessage,
} from "../types";
import { retainedMentionIds } from "../lib/mentions";
import { useMentions } from "../hooks/use-mentions";
import { UploadPreview } from "./upload-preview";
import { AttachmentDraft } from "./attachment-draft";
import { HOVER_MUTED, HOVER_MUTED_TEXT } from "./styles";

const ACCEPTED_MEDIA = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/webm",
];

// The Input primitive's class list (minus its `file:` utilities, which only
// apply to file inputs) so the textarea matches the host's fields.
const INPUT_CLASS = cn(
  "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
);

const TOOLBAR_BUTTON_CLASS =
  "mb-0.5 size-11 min-h-11 min-w-11 shrink-0 cursor-pointer rounded-full motion-reduce:transition-none";

const PICKER_BUTTON_CLASS = cn(
  "min-h-11 min-w-11 cursor-pointer text-xl",
  HOVER_MUTED,
);

interface ComposerProps {
  chatId: string;
  storageKey: string;
  serverDraft: string;
  title: string;
  participants: Participant[];
  mediaLimit: number;
  mediaEnabled: boolean;
  disabled: boolean;
  open: boolean;
  reply: Message | null;
  editing: Message | null;
  onCancel: () => void;
  onSend: (item: PendingMessage) => void;
  onEdit: (message: Message, text: string, mentions: string[]) => Promise<void>;
  onDraft: (draft: string) => void;
  onError: (message: string) => void;
  onTyping?: (active: boolean) => void;
}

export function Composer({
  chatId,
  storageKey,
  serverDraft,
  title,
  participants,
  mediaLimit,
  mediaEnabled,
  disabled,
  open,
  reply,
  editing,
  onCancel,
  onSend,
  onEdit,
  onDraft,
  onError,
  onTyping,
}: ComposerProps) {
  const voiceEnabled = useHandlerCompanionEnabled();
  const [preferredInputMode, setInputMode] = useState<
    "voice" | "text" | "review"
  >(voiceEnabled ? "voice" : "text");
  const inputMode = editing ? "text" : preferredInputMode;
  const localDraft = useChatStore((state) => state.drafts[storageKey]);
  const setDraft = useChatStore((state) => state.setDraft);
  const draft = localDraft ?? serverDraft;
  const storedMentions = useChatStore(
    (state) => state.draftMentions[storageKey],
  );
  const setDraftMentions = useChatStore((state) => state.setDraftMentions);
  const [editMentions, setEditMentions] = useState<{
    rootId: string;
    ids: string[];
  } | null>(null);
  const [editDraft, setEditDraft] = useState<{
    rootId: string;
    text: string;
  } | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const upload = useUpload(chatId);
  const [uploading, setUploading] = useState<{
    file: File;
    percent: number | null;
  } | null>(null);
  const uploadController = useRef<AbortController | null>(null);
  useEffect(() => () => uploadController.current?.abort(), []);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const optionsId = useId();
  const text = editing
    ? editDraft?.rootId === editing.rootId
      ? editDraft.text
      : editing.text
    : draft;
  const tooLong = text.length > 4000;
  const setText = (value: string) =>
    editing
      ? setEditDraft({ rootId: editing.rootId, text: value })
      : setDraft(storageKey, value);
  const selectedMentions = editing
    ? editMentions?.rootId === editing.rootId
      ? editMentions.ids
      : (editing.mentions ?? []).map((mention) => mention.memberId)
    : (storedMentions ?? []);
  const validMentions = retainedMentionIds(
    text,
    selectedMentions,
    participants,
  );
  const setMentionIds = (ids: string[]) =>
    editing
      ? setEditMentions({ rootId: editing.rootId, ids })
      : setDraftMentions(storageKey, ids);
  const mention = useMentions({
    text,
    participants,
    selected: validMentions,
    setText,
    setSelected: setMentionIds,
    input,
  });
  useEffect(() => {
    const node = input.current;
    if (!node || !open || inputMode === "voice") return;
    const resize = () => {
      node.style.height = "auto";
      const height = Math.min(node.scrollHeight, 200);
      node.style.height = `${height}px`;
      node.style.overflowY = node.scrollHeight > 200 ? "auto" : "hidden";
    };
    resize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (width !== node.clientWidth) {
        width = node.clientWidth;
        resize();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, open, inputMode]);
  const editRoot = editing?.rootId;
  useEffect(() => {
    if (!open || inputMode === "voice") return;
    // On phones focus raises the keyboard over the transcript the member
    // opened to read; only a reply or edit asks to type straight away.
    if (
      !editRoot &&
      !reply?.rootId &&
      window.matchMedia?.("(pointer: coarse)").matches
    )
      return;
    input.current?.focus();
  }, [open, editRoot, reply?.rootId, inputMode]);
  useEffect(() => {
    if (
      localDraft === undefined ||
      localDraft === serverDraft ||
      localDraft.length > 4000
    )
      return;
    const timer = setTimeout(() => onDraft(localDraft), 650);
    return () => clearTimeout(timer);
  }, [localDraft, serverDraft, onDraft]);
  const ready = attachments.every(
    (attachment) => attachment.status === "CLEAR",
  );
  const updateAttachment = useCallback((attachment: Attachment) => {
    setAttachments((items) =>
      items.map((item) =>
        item.attachId === attachment.attachId ? attachment : item,
      ),
    );
  }, []);
  const addFiles = async (files: FileList | null) => {
    if (!files || uploadController.current) return;
    const selected = [...files];
    if (selected.length + attachments.length > 4) {
      onError("Attach up to four images or videos per message.");
      return;
    }
    const controller = new AbortController();
    uploadController.current = controller;
    for (const file of selected) {
      if (controller.signal.aborted) break;
      if (!ACCEPTED_MEDIA.includes(file.type)) {
        onError("Choose a PNG, JPEG, WebP, GIF, MP4 or WebM file.");
        continue;
      }
      if (file.size > mediaLimit) {
        onError(
          `Choose media under ${Math.floor(mediaLimit / 1024 / 1024)} MB.`,
        );
        continue;
      }
      try {
        setUploading({ file, percent: null });
        const result = await upload.mutateAsync({
          file,
          signal: controller.signal,
          onProgress: (percent) => setUploading({ file, percent }),
        });
        if (controller.signal.aborted) break;
        setAttachments((items) => [...items, result.attachment]);
      } catch (error) {
        if (controller.signal.aborted) break;
        onError(
          error instanceof Error
            ? error.message
            : "Upload failed. Choose the file again to retry.",
        );
      }
    }
    uploadController.current = null;
    setUploading(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      disabled ||
      tooLong ||
      Boolean(uploading) ||
      (!editing && !ready) ||
      (!text.trim() && (Boolean(editing) || !attachments.length))
    )
      return;
    onTyping?.(false);
    if (editing) {
      try {
        await onEdit(editing, text, validMentions);
      } catch {
        /* The conversation displays the mutation error and retains the edit. */
      }
      return;
    }
    onSend({
      reqKey: crypto.randomUUID(),
      text,
      replyTo: reply?.rootId,
      mentions: validMentions,
      attachmentIds: attachments.map((item) => item.attachId),
      state: "sending",
    });
    setDraft(storageKey, "");
    setDraftMentions(storageKey, []);
    onDraft("");
    setAttachments([]);
    onCancel();
    if (voiceEnabled && inputMode === "review") setInputMode("voice");
  };
  return (
    <form
      className="sm-chat-composer relative shrink-0 border-t border-border bg-card/90 px-2 pt-2 pb-[max(8px,env(safe-area-inset-bottom))] backdrop-blur-sm in-data-keyboard:pb-2"
      onSubmit={(event) => void submit(event)}
    >
      {tooLong && (
        <p role="alert" className="mb-2 text-sm text-destructive">
          Your message is {text.length - 4000} characters over the
          4,000-character limit. Shorten it before sending; your draft is kept
          here.
        </p>
      )}
      {(reply || editing) && (
        <div className="mb-2 flex items-start gap-2 rounded-sm border-l-2 border-border bg-muted/60 py-1.5 pr-2.5 pl-2">
          <span className="max-h-[55px] min-w-0 flex-1 overflow-hidden text-xs text-ellipsis text-muted-foreground">
            <strong className="block text-xs text-gold">
              {editing
                ? "Editing message"
                : `Replying to ${reply?.memberName ?? "message"}`}
            </strong>
            {(editing ?? reply)?.text || "Attachment"}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "size-11 min-h-11 min-w-11 shrink-0 cursor-pointer rounded-full text-muted-foreground",
              HOVER_MUTED_TEXT,
            )}
            onClick={onCancel}
            aria-label="Cancel reply or edit"
          >
            <X />
          </Button>
        </div>
      )}
      {uploading && (
        <UploadPreview file={uploading.file} percent={uploading.percent} />
      )}
      {!editing && attachments.length > 0 && (
        <div className="flex max-h-[140px] flex-col gap-1.5 overflow-auto">
          {attachments.map((attachment) => (
            <AttachmentDraft
              key={attachment.attachId}
              chatId={chatId}
              attachment={attachment}
              onReady={updateAttachment}
              onRemove={() =>
                setAttachments((items) =>
                  items.filter((item) => item.attachId !== attachment.attachId),
                )
              }
            />
          ))}
        </div>
      )}
      {mention.matches.length > 0 && (
        <div
          id={optionsId}
          className="absolute inset-x-2 bottom-[calc(100%+4px)] z-2 max-h-[170px] overflow-y-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-[0_5px_25px_rgb(0_0_0/45%)]"
          role="listbox"
          aria-label="Mention a participant"
        >
          {mention.matches.map((participant, index) => (
            <Button
              key={participant.memberId}
              id={`${optionsId}-${participant.memberId}`}
              type="button"
              variant="ghost"
              className="h-auto min-h-11 w-full cursor-pointer justify-between px-3 py-2 font-normal hover:bg-muted aria-selected:bg-muted aria-selected:text-gold motion-reduce:transition-none dark:hover:bg-muted"
              role="option"
              aria-selected={mention.index === index}
              aria-label={`@${participant.name} ${participant.role === "ASSISTANT" ? "AI assistant" : "Participant"}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => mention.choose(participant)}
            >
              <span>@{participant.name}</span>
              <small className="text-xs text-muted-foreground">
                {participant.role === "ASSISTANT"
                  ? "AI assistant"
                  : "Participant"}
              </small>
            </Button>
          ))}
        </div>
      )}
      {validMentions.length > 0 && (
        <div
          className="flex flex-wrap gap-1 pb-1.5"
          aria-label="Selected mentions"
        >
          {validMentions.map((id) => (
            <Button
              key={id}
              type="button"
              variant="secondary"
              size="sm"
              className="min-h-11 cursor-pointer gap-0 px-1.5 text-xs font-normal text-gold-soft motion-reduce:transition-none"
              aria-label={`Remove mention ${participants.find((participant) => participant.memberId === id)?.name}`}
              onClick={() =>
                setMentionIds(validMentions.filter((item) => item !== id))
              }
            >
              @
              {
                participants.find((participant) => participant.memberId === id)
                  ?.name
              }
              <X className="ml-1 size-[11px]" />
            </Button>
          ))}
        </div>
      )}
      {emojiOpen && (
        <div
          className="flex flex-wrap gap-1 p-1.5 text-xl"
          aria-label="Insert emoji"
        >
          {REACTIONS.map(({ emoji, label }) => (
            <Button
              key={emoji}
              type="button"
              variant="ghost"
              size="icon"
              className={PICKER_BUTTON_CLASS}
              aria-label={`Insert ${label}`}
              onClick={() => {
                setText((text + emoji).slice(0, 4000));
                setEmojiOpen(false);
                input.current?.focus();
              }}
            >
              {emoji}
            </Button>
          ))}
        </div>
      )}
      {voiceEnabled && inputMode === "voice" && open && (
        <VoiceComposer
          disabled={disabled || Boolean(uploading)}
          onType={() => setInputMode("text")}
          onTranscript={(transcript) => {
            setText([text.trim(), transcript].filter(Boolean).join(" "));
            setInputMode("review");
          }}
        />
      )}
      {voiceEnabled && inputMode === "review" && (
        <p className="mb-2 text-center text-xs text-muted-foreground">
          Check your words before sending
        </p>
      )}
      <div
        className="flex items-end gap-1.5 hidden:hidden"
        hidden={voiceEnabled && inputMode === "voice"}
      >
        {mediaEnabled && (
          <>
            <input
              ref={fileInput}
              className="sr-only"
              type="file"
              accept={ACCEPTED_MEDIA.join(",")}
              multiple
              tabIndex={-1}
              onChange={(event) => {
                void addFiles(event.currentTarget.files);
                event.currentTarget.value = "";
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                TOOLBAR_BUTTON_CLASS,
                "text-muted-foreground hover:bg-muted hover:text-foreground dark:hover:bg-muted",
              )}
              aria-label="Attach image or video"
              title="Attach image or video"
              disabled={
                Boolean(editing) ||
                Boolean(uploading) ||
                attachments.length >= 4
              }
              onClick={() => fileInput.current?.click()}
            >
              <Paperclip className="size-5" />
            </Button>
          </>
        )}
        <div className="relative min-w-0 flex-1">
          {" "}
          <label className="sr-only" htmlFor={inputId}>
            Message {title}
          </label>
          <textarea
            ref={input}
            id={inputId}
            className={cn(
              INPUT_CLASS,
              "block h-auto max-h-[200px] min-h-11 resize-none rounded-2xl py-2 pr-[52px] pl-3.5 font-sans leading-normal md:text-base",
            )}
            placeholder={
              participants.length > 2
                ? "Message (type @ to mention)"
                : "Message"
            }
            value={text}
            maxLength={4000}
            rows={1}
            dir="auto"
            enterKeyHint="send"
            aria-autocomplete="list"
            aria-controls={mention.matches.length ? optionsId : undefined}
            aria-activedescendant={
              mention.matches.length
                ? `${optionsId}-${mention.matches[mention.index].memberId}`
                : undefined
            }
            onSelect={(event) =>
              mention.setCaret(event.currentTarget.selectionStart)
            }
            onChange={(event) => {
              setText(event.target.value);
              onTyping?.(Boolean(event.target.value.trim()));
              mention.setCaret(event.currentTarget.selectionStart);
            }}
            onBlur={() => onTyping?.(false)}
            onKeyDown={(event) => {
              if (mention.onKeyDown(event)) return;
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing &&
                event.keyCode !== 229
              ) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              TOOLBAR_BUTTON_CLASS,
              "absolute right-0.5 bottom-0 mb-0 bg-card text-muted-foreground hover:bg-muted hover:text-foreground dark:bg-card dark:hover:bg-muted",
            )}
            aria-label="Insert emoji"
            title="Insert emoji"
            aria-expanded={emojiOpen}
            onClick={() => setEmojiOpen(!emojiOpen)}
          >
            <Smile className="size-[18px]" />
          </Button>
        </div>
        <Button
          className={TOOLBAR_BUTTON_CLASS}
          size="icon"
          type="submit"
          aria-label={editing ? "Save edit" : "Send message"}
          disabled={
            disabled ||
            tooLong ||
            Boolean(uploading) ||
            (!editing && !ready) ||
            (!text.trim() && (Boolean(editing) || !attachments.length))
          }
        >
          <ArrowUp className="size-5" />
          <span className="sr-only">{editing ? "Save" : "Send"}</span>
        </Button>
      </div>
      {voiceEnabled && !editing && inputMode !== "voice" && (
        <div className="sm-chat-voice-options">
          {inputMode === "review" && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setText("");
                setInputMode("voice");
              }}
            >
              Re-record
            </button>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setInputMode("voice")}
          >
            {inputMode === "review" ? "Add more" : "Use voice"}
          </button>
        </div>
      )}
    </form>
  );
}
